import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { enrollCourses } from '../../utils/enrollmentStore';
import { PLACEHOLDER_COURSE_IMAGE } from '../../constants';
import { isPaidCourse } from '../../utils/priceUtils';
import './Cart.css';

export default function Cart() {
  const { items, removeItem } = useCart();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [checkingOut, setCheckingOut] = useState(false);
  const [error, setError] = useState(null);

  const handleCheckout = () => {
    if (!isAuthenticated || !user) {
      navigate('/login', { state: { from: '/cart' } });
      return;
    }
    if (items.length === 0) return;

    const freeItems = items.filter((item) => !isPaidCourse(item.price));
    const paidItems = items.filter((item) => isPaidCourse(item.price));

    if (freeItems.length === 0 && paidItems.length > 0) {
      setError('Paid courses must be paid for individually. Open each course page and use "Pay & enroll" so the owner can approve your access.');
      return;
    }

    setError(null);
    setCheckingOut(true);

    try {
      if (freeItems.length > 0) {
        enrollCourses(user.uid, freeItems);
        freeItems.forEach((item) => removeItem(item.id));
      }
      navigate('/my-learning');
    } catch {
      setError('Could not enroll in courses. Please try again.');
    } finally {
      setCheckingOut(false);
    }
  };

  if (items.length === 0 && !checkingOut) {
    return (
      <div className="cart cart-empty">
        <div className="cart-inner">
          <h1 className="cart-title">Your cart</h1>
          <div className="cart-empty-state">
            <p className="cart-empty-text">Your cart is empty.</p>
            <Link to="/" className="cart-empty-link">Browse courses</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart">
      <div className="cart-inner">
        <h1 className="cart-title">Your cart</h1>
        {error && <p className="cart-error" role="alert">{error}</p>}
        <ul className="cart-list">
          {items.map((item) => (
            <li key={item.id} className="cart-item">
              <Link to={`/course/${item.id}`} className="cart-item-link">
                <img src={item.thumbnail || PLACEHOLDER_COURSE_IMAGE} alt="" className="cart-item-img" onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_COURSE_IMAGE; }} />
                <div className="cart-item-body">
                  <span className="cart-item-title">{item.title}</span>
                  <span className="cart-item-meta">{item.instructor} · Free</span>
                </div>
              </Link>
              <button
                type="button"
                className="cart-item-remove"
                onClick={() => removeItem(item.id)}
                aria-label={`Remove ${item.title} from cart`}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
        <div className="cart-footer">
          <button
            type="button"
            className="cart-checkout-btn"
            onClick={handleCheckout}
            disabled={checkingOut}
          >
            {checkingOut ? 'Enrolling…' : isAuthenticated ? 'Enroll in all' : 'Log in to enroll'}
          </button>
          {!isAuthenticated && items.length > 0 && (
            <p className="cart-login-hint">You’ll need to log in to enroll in these courses.</p>
          )}
        </div>
      </div>
    </div>
  );
}
